<template>
  <div class="pui-wrapper pui-settings-editor">
    <pui-flex class="editor-actionbar" alignItems="center">
      <div
        :class="{ active: showTemplateCode }"
        @click="displayTemplate"
        class="actionbar-item tab-btn"
      >
        {{ $t('poster.editor.template.title') }}
      </div>
      <div
        :class="{ active: showBindingsCode }"
        @click="displayBindings"
        class="actionbar-item tab-btn"
      >
        {{ $t('poster.editor.bindings.title') }}
      </div>
      <div :class="{ active: showTypesCode }" @click="displayTypes" class="actionbar-item tab-btn">
        {{ $t('poster.editor.types.title') }}
      </div>

      <div class="actionbar-item padded">
        <pui-flex
          v-if="renderSuccess"
          :title="$t('poster.editor.state.up_to_date')"
          alignItems="center"
        >
          <span
            class="picto mdi mdi-check"
            style="color: green; font-size: 1.8em; margin-right: 5px"
          >
          </span>
          <span> {{ renderTime }} ms </span>
        </pui-flex>
        <div v-if="renderInProgress" style="position: relative;">
          <span
            class="picto mdi mdi-sync"
            style="color: orange; font-size: 1.8em; visibility: hidden"
          ></span>
          <span
            :title="$t('poster.editor.state.in_progress')"
            class="picto mdi mdi-sync status-rotate"
            style="color: orange; font-size: 1.8em"
          ></span>
        </div>
        <span
          v-if="!renderInProgress && !renderSuccess && !renderError"
          :title="$t('poster.editor.state.manual_refresh')"
          class="picto mdi mdi-sync"
          style="color: red; font-size: 1.8em"
        ></span>
        <span
          v-if="renderError"
          :title="$t('poster.editor.state.error')"
          class="picto mdi mdi-alert"
          style="color: red; font-size: 1.8em"
        ></span>
      </div>

      <pui-flex
        class="actionbar-item padded editor-actions"
        style="margin-left: auto"
        wrap="wrap"
        justifyContent="end"
        alignItems="center"
      >
        <pui-checkbox
          :showLabel="true"
          :checked="refreshAuto"
          :label="$t('poster.editor.actions.autoRefresh')"
          @change="onRefreshAutoChange"
        ></pui-checkbox>
        <pui-button
          v-if="!refreshAuto"
          @click="updatePreviewHook(true)"
          variant="primary"
          class="action-btn"
          picto="mdi-sync"
        >
          {{ $t('poster.editor.actions.manual_refresh') }}
        </pui-button>
        <pui-button
          @click="autoFormat"
          :title="$t('poster.editor.actions.autoformat')"
          variant="primary"
          class="action-btn"
          picto="mdi-auto-fix"
        >
          {{ $t('poster.editor.actions.autoformat') }}
        </pui-button>
        <pui-button
          :disabled="!unsavedValues"
          @click="saveData"
          :title="$t('poster.editor.actions.save')"
          variant="primary"
          class="action-btn"
          picto="mdi-content-save"
        >
          {{ $t('poster.editor.actions.save') }}
        </pui-button>
        <pui-menu-button
          :buttonProps="{ outline: false, variant: 'primary', flat: false }"
          class="actions-menu-button"
        >
          <template #default="{closePopper}">
            <pui-flex direction="column" class="submenu-actions-wrapper">
              <pui-button
                @click="resetEditor"
                :title="$t('poster.editor.actions.reset_defaults')"
                block
                flat
                class="action-btn"
                picto="mdi-restore"
              >
                {{ $t('poster.editor.actions.reset_defaults') }}
              </pui-button>
              <pui-button
                @click="
                  closePopper();
                  clearConsole();
                "
                :title="$t('poster.editor.actions.clear_console')"
                block
                flat
                class="action-btn"
                picto="mdi-close"
              >
                {{ $t('poster.editor.actions.clear_console') }}
              </pui-button>
              <pui-button
                @click="
                  closePopper();
                  removeComponentStorage();
                "
                :title="$t('poster.editor.actions.clear_cache')"
                block
                flat
                class="action-btn"
                picto="mdi-close"
              >
                {{ $t('poster.editor.actions.clear_cache') }}
              </pui-button>
            </pui-flex>
          </template>
        </pui-menu-button>
        <pui-button @click="openManualDialog" variant="primary" picto="mdi-help"> </pui-button>
      </pui-flex>
    </pui-flex>

    <div class="editor-wrapper">
      <pui-template-toolbar
        :disabled="!showTemplateCode"
        @insertComponent="insertComponent"
        class="editor-toolbar"
      ></pui-template-toolbar>

      <div class="editor-json">
        <div v-show="showTemplateCode" class="editor-json-template">
          <json-editor
            key="template"
            ref="templateEditor"
            :placeholder="$t('poster.editor.template.placeholder')"
            :value="templateString"
            @input="onTemplateChange"
          />
        </div>
        <div v-show="showBindingsCode" class="editor-json-bindings">
          <json-editor
            key="bindings"
            ref="bindingsEditor"
            :value="posterValuesString"
            @input="onBindingsChange"
          />
        </div>
        <div v-show="showTypesCode" class="editor-json-types">
          <json-editor key="types" ref="typesEditor" :value="typesString" @input="onTypesChange" />
        </div>
      </div>

      <div class="editor-viewer">
        <div class="bea-preview">
          <div class="preview-viewer">
            <div
              v-if="parsedTemplate == null || !parsedTemplate.pages || posterValues == null"
              class="preview-image"
            >
              <img :src="imgPdf" alt="preview" />
            </div>
            <div v-else-if="templateIsInvalid()" class="preview-image">
              <img :src="imgErrorTemplate" alt="preview" />
            </div>
            <pui-svg-signage-preview
              ref="preview"
              v-else
              :bindings="renderValues"
              :types="renderTypes"
              :template="parsedTemplate"
              :showAllPrintables="true"
              :showPage="false"
              :showShadow="false"
              class="preview-image-auto"
            />
          </div>
        </div>
      </div>
    </div>
    <pui-manual-editor-dialog ref="manualEditorDialog"></pui-manual-editor-dialog>
  </div>
</template>
<script>
import RenderManager from 'piivo-poster-engine/src/lib/renderer/renderManager';
import { getComponentsManager } from 'piivo-poster-engine/src/lib/services/componentsManager';
import { getFontsManager } from 'piivo-poster-engine/src/lib/services/fontsManager';
import { TemplatesManager } from 'piivo-poster-engine/src/lib/services/templatesManager';

import { debounce } from '../../../utils/debounce';
import jsonEditor from '../components/editor/JsonEditor';
import puiManualEditorDialog from '../components/editor/ManualEditorDialog';
import puiTemplateToolbar from '../components/editor/TemplateToolbar.vue';
import { DEFAULT_TEMPLATE, DEFAULT_TYPES, DEFAULT_VALUES } from '../constants/editor';
import imgErrorTemplate from '../static/img/error-template.jpg';
import imgPdf from '../static/img/pdf.jpg';

export default {
  components: {
    jsonEditor,
    puiManualEditorDialog,
    puiTemplateToolbar,
  },
  data() {
    return {
      // Tabs
      showTemplateCode: true,
      showBindingsCode: false,
      showTypesCode: false,
      // Editor values
      templateString: JSON.stringify(DEFAULT_TEMPLATE, null, 2),
      template: JSON.parse(JSON.stringify(DEFAULT_TEMPLATE)),
      posterValuesString: JSON.stringify(DEFAULT_VALUES, null, 2),
      posterValues: JSON.parse(JSON.stringify(DEFAULT_VALUES)),
      typesString: JSON.stringify(DEFAULT_TYPES, null, 2),
      types: JSON.parse(JSON.stringify(DEFAULT_TYPES)),
      // Render object values
      parsedTemplate: null,
      renderValues: {},
      renderTypes: {},
      // Other render state
      refreshAuto: false,
      renderInProgress: true,
      renderError: false,
      renderSuccess: false,
      /**
       * If the template, poster values, types are unsaved
       * (not just poster values)
       */
      unsavedValues: false,
      renderTime: 0,
      // OTHER
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      debouncedSaveData: () => {},
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      debouncedUpdatePreviewFunc: () => {},
      imgErrorTemplate,
      imgPdf,
    };
  },
  methods: {
    displayTemplate() {
      this.showTemplateCode = true;
      this.showBindingsCode = false;
      this.showTypesCode = false;
    },
    displayBindings() {
      this.showTemplateCode = false;
      this.showBindingsCode = true;
      this.showTypesCode = false;

      // Since the editor was hidden, it did not initialize its content
      // now that it's visible, refresh its view
      this.$refs.bindingsEditor.refresh();
    },
    displayTypes() {
      this.showTemplateCode = false;
      this.showBindingsCode = false;
      this.showTypesCode = true;

      this.$refs.typesEditor.refresh();
    },
    templateIsInvalid() {
      // Verify if the template doesn't fulfill prerequisites
      return TemplatesManager.isNotCorrect(this.parsedTemplate);
    },
    onTemplateChange(codeValue) {
      this.templateString = codeValue;
      this.renderSuccess = false;
      this.renderError = false;
      this.renderInProgress = false;
      this.unsavedValues = true;

      try {
        this.datasAreSaved = false;
        this.template = JSON.parse(codeValue);
        if (this.refreshAuto) {
          this.updatePreviewHook(false);
        }
      } catch (e) {
        this.renderSuccess = false;
        this.renderInProgress = false;
        this.renderError = true;
      }
    },
    /**
     * Inserts the component into the template editor
     *
     * @param {object} componentObj - the component object to insert
     */
    insertComponent(componentObj) {
      if (this.$refs.templateEditor) {
        let componentStr = JSON.stringify(componentObj, null, 2);
        const ranges = this.$refs.templateEditor.getSelectionRanges();
        const range = ranges && ranges.length ? ranges[0] : { from: 0, to: 0 };
        if (
          this.templateString &&
          ranges &&
          ranges.length &&
          range.from < this.templateString.length - 1
        ) {
          componentStr = `${componentStr},`; // Add trailing comma
        }
        const res = this.$refs.templateEditor.insert(componentStr, range.from, range.to);
        if (res) {
          this.autoFormat();
        }
      }
    },
    onBindingsChange(codeValue) {
      this.posterValuesString = codeValue;
      this.renderSuccess = false;
      this.renderError = false;
      this.renderInProgress = false;
      this.unsavedValues = true;

      try {
        this.datasAreSaved = false;
        this.posterValues = JSON.parse(codeValue);
        if (this.refreshAuto) {
          this.updatePreviewHook(false);
        }
      } catch (e) {
        this.renderSuccess = false;
        this.renderInProgress = false;
        this.renderError = true;
      }
    },
    onTypesChange(codeValue) {
      this.typesString = codeValue;
      this.renderSuccess = false;
      this.renderError = false;
      this.renderInProgress = false;
      this.unsavedValues = true;

      try {
        this.datasAreSaved = false;
        this.types = JSON.parse(codeValue);
        if (this.refreshAuto) {
          this.updatePreviewHook(false);
        }
      } catch (e) {
        this.renderSuccess = false;
        this.renderInProgress = false;
        this.renderError = true;
      }
    },
    /**
     * @param {boolean} auto - if auto refresh should be enabled
     */
    onRefreshAutoChange(auto) {
      this.refreshAuto = auto;
      if (this.refreshAuto) {
        this.updatePreviewHook(true);
      }
    },
    resetEditor() {
      this.initValues(
        JSON.stringify(DEFAULT_TEMPLATE),
        JSON.stringify(DEFAULT_VALUES),
        JSON.stringify(DEFAULT_TYPES)
      );

      this.updatePreviewHook(true);
    },
    async updatePreview() {
      if (this.template === null) {
        this.renderSuccess = false;
        this.renderInProgress = false;
        this.renderError = true;
        return;
      }

      const start = performance.now();

      this.renderInProgress = true;
      this.renderError = false;

      try {
        this.renderValues = JSON.parse(JSON.stringify(this.posterValues));
        this.renderTypes = JSON.parse(JSON.stringify(this.types));
        this.parsedTemplate = await getComponentsManager().parseTemplateComponents(
          this.template,
          this.posterValues
        );
        await getFontsManager().registerAllFontsIfEnabled(this.parsedTemplate);
        RenderManager.searchNodeToInvalidate(
          this.parsedTemplate,
          this.renderValues,
          this.renderTypes,
          null,
          true
        );
        this.renderSuccess = true;
      } catch (error) {
        this.renderSuccess = false;
        this.renderError = true;
        console.error('Error during rendering : ', error);
        return;
      } finally {
        this.renderInProgress = false;
        this.$nextTick(() => {
          const end = performance.now();
          this.renderTime = Math.round(end - start);
        });
      }
    },
    updatePreviewHook(immediate) {
      if (immediate) {
        this.updatePreview();
      } else {
        this.debouncedUpdatePreviewFunc();
      }
      this.saveDataHook(false);
    },
    clearConsole() {
      console.clear();
    },
    /**
     * On windows keydown
     *
     * @param {KeyboardEvent} event - the key event
     */
    onKeyDown(event) {
      if ((event.ctrlKey && event.key === 's') || (event.metaKey && event.key === 's')) {
        // Prevent the Save dialog to open
        event.preventDefault();
        if (!this.refreshAuto) {
          this.updatePreviewHook(true);
        }
        this.saveDataHook(true);
        return false;
      }
    },
    saveDataHook(immediate = false) {
      if (immediate) {
        this.saveData();
      } else {
        this.debouncedSaveData();
      }
    },
    /**
     * Saves template, bindings, types to localStorage
     */
    saveData() {
      try {
        localStorage.setItem('poster_editor_refresh_auto', JSON.stringify(this.refreshAuto));
        if (this.template !== null) {
          localStorage.setItem('poster_editor_template', this.templateString);
        }
        if (this.posterValuesString !== '') {
          localStorage.setItem('poster_editor_values', this.posterValuesString);
        }
        if (JSON.stringify(this.types) !== '') {
          localStorage.setItem('poster_editor_types', this.typesString);
        }
        this.datasAreSaved = true;
        this.unsavedValues = false;
      } catch (error) {
        console.error('Error when saving datas : ', error);
      }
    },
    /**
     * Autoformats all values
     */
    autoFormat() {
      // Formatting is done by default in 'initValues', so just reexecute it
      // on the current values
      this.initValues(this.templateString, this.posterValuesString, this.typesString);
    },
    /**
     * Clears the template components cache
     */
    removeComponentStorage() {
      getComponentsManager().clearComponentsCache();
      this.updatePreviewHook(true);
    },
    /**
     * Display alert when browser or tab are killed
     * @param {Object} e - event
     * @returns {string}
     */
    eventUnloadFunction(e) {
      if (!this.datasAreSaved) {
        e = e || window.event;
        if (e) {
          e.returnValue = '';
        }
        return '';
      }
    },
    restoreEditor() {
      this.refreshAuto = JSON.parse(localStorage.getItem('poster_editor_refresh_auto') || 'false');
      this.initValues(
        localStorage.getItem('poster_editor_template'),
        localStorage.getItem('poster_editor_values'),
        localStorage.getItem('poster_editor_types')
      );
      this.updatePreviewHook(true);
    },
    openManualDialog() {
      this.$refs.manualEditorDialog.open();
    },
    /**
     * Initializes the string and object values of the template
     *
     * @param {string} templateStr - raw template string
     * @param {string} valuesStr - raw values string
     * @param {string} typesStr - raw types string
     */
    initValues(templateStr, valuesStr, typesStr) {
      this.templateString = templateStr ? JSON.stringify(JSON.parse(templateStr), null, 2) : null;
      this.template = this.templateString ? JSON.parse(this.templateString) : null;

      this.posterValuesString = valuesStr
        ? JSON.stringify(JSON.parse(valuesStr), null, 2)
        : JSON.stringify(this.posterValues || {});
      this.posterValues = this.posterValuesString
        ? JSON.parse(this.posterValuesString)
        : this.posterValues;

      this.typesString = typesStr
        ? JSON.stringify(JSON.parse(typesStr), null, 2)
        : JSON.stringify(this.types || {});
      this.types = this.typesString ? JSON.parse(this.typesString) : this.types;
    },
  },
  /**
   * Component before mount hook
   */
  beforeMount() {
    this.debouncedSaveData = debounce(this.saveData, 500, false);
    this.debouncedUpdatePreviewFunc = debounce(this.updatePreview, 500, false);
    this.restoreEditor();
  },
  /**
   * Component mounted hook
   */
  mounted() {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('beforeunload', this.eventUnloadFunction);
  },
  /**
   * Component destroyed hook
   */
  destroyed() {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('beforeunload', this.eventUnloadFunction);
  },
  /**
   * Vue-router hook, before route changes
   */
  beforeRouteLeave(to, from, next) {
    if (this.datasAreSaved) {
      next();
    } else {
      if (window.confirm('Attention vous quittez la page sans avoir sauvegarder vos éléments')) {
        next();
      }
    }
  },
};
</script>
